var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { CustomElementUtils, CustomElement, P, Watch, PropertyConverters, PropertyChangeEvent, PropertyChangeEventName, Debounce, Utils, Logging, Components } from '@pacem/pacem-core';
import { Strings } from '@pacem/pacem-foundation';
import { Pacem3DAdapterElement, StalePropertyFlag } from '../../types';
import { Renderers, WebGPUIssueCause } from './renderer';
//namespace Pacem.Components.Drawing3D {
const WEBGPU_RAYCAST_CALLBACK_PROP = 'WebGPURaycastCallback';
const WEBGPU_RAYCAST_PIXEL_PROP = 'WebGPURaycastPixel';
const GETVAL = CustomElementUtils.getAttachedPropertyValue, SETVAL = CustomElementUtils.setAttachedPropertyValue, DELVAL = CustomElementUtils.deleteAttachedPropertyValue;
let Pacem3DWgslScriptElement = class Pacem3DWgslScriptElement extends Components.PacemItemElement {
    findContainer() {
        return CustomElementUtils.findAncestor(this, n => n instanceof Pacem3DWebgpuAdapterElement);
    }
    #version = '';
    get version() {
        return this.#version;
    }
    #observer = new MutationObserver(() => {
        this.wgsl = this.textContent;
    });
    viewActivatedCallback() {
        super.viewActivatedCallback();
        this.wgsl = this.textContent;
        this.#observer.observe(this, { characterData: true });
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (name === 'wgsl') {
            this._computeVersion(val);
        }
    }
    _computeVersion(wgsl = this.wgsl) {
        Strings.hash(wgsl).then(v => {
            if (v !== this.#version) {
                const old = this.#version;
                const val = v;
                this.#version = val;
                const evt = new PropertyChangeEvent({ propertyName: 'version', oldValue: old, currentValue: val, firstChange: Utils.isNullOrEmpty(old) });
                this.dispatchEvent(evt);
            }
        });
    }
    disconnectedCallback() {
        this.#observer.disconnect();
        super.disconnectedCallback();
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], Pacem3DWgslScriptElement.prototype, "type", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], Pacem3DWgslScriptElement.prototype, "wgsl", void 0);
Pacem3DWgslScriptElement = __decorate([
    CustomElement({ tagName: P + '-3d-wgsl-script' })
], Pacem3DWgslScriptElement);
export { Pacem3DWgslScriptElement };
let Pacem3DWebgpuAdapterElement = class Pacem3DWebgpuAdapterElement extends Pacem3DAdapterElement {
    constructor() {
        super(...arguments);
        this.#items = [];
        this._scriptVersionChangeHandler = (evt) => {
            const { propertyName: name } = evt.detail;
            if (name === 'version') {
                this._parseWgslModifiers(evt.detail.currentValue);
            }
        };
        this.#scenes = new Set();
        this.#renderers = new Map();
        this.#supported = true;
        this.#active = true;
        this.#sceneToUpdate = new WeakMap();
    }
    invalidateSize(stage, size = stage.size) {
        const renderer = this._ensureRenderer(stage);
        renderer?.resize(size);
    }
    #items;
    get items() {
        return this.#items;
    }
    register(item) {
        const items = this.#items;
        if (item instanceof Pacem3DWgslScriptElement && !items.includes(item)) {
            item.addEventListener(PropertyChangeEventName, this._scriptVersionChangeHandler, false);
            items.push(item);
            this._parseWgslModifiers(this.modifiers?.version);
            return true;
        }
        return false;
    }
    unregister(item) {
        const items = this.#items;
        const ndx = items.indexOf(item);
        if (ndx >= 0) {
            item.removeEventListener(PropertyChangeEventName, this._scriptVersionChangeHandler, false);
            items.splice(ndx, 1);
            this._parseWgslModifiers(this.modifiers?.version);
            return true;
        }
        return false;
    }
    #scenes;
    #renderers;
    #supported;
    #active;
    get supported() {
        return this.#supported;
    }
    get active() {
        return this.#active;
    }
    _webGPUNotSupported(issue) {
        if (this.#supported && issue.cause === WebGPUIssueCause.NotSupported) {
            this.#supported = false;
            this.dispatchEvent(new PropertyChangeEvent({ propertyName: 'supported', oldValue: null, currentValue: false }));
        }
        if (this.#active) {
            this.#active = false;
            this.dispatchEvent(new PropertyChangeEvent({ propertyName: 'active', oldValue: null, currentValue: false }));
        }
    }
    initialize(scene) {
        const set = this.#renderers;
        const scenes = this.#scenes;
        if (!scenes.has(scene)) {
            scenes.add(scene);
        }
        return Renderers.create(scene, { enableValidation: this.enableValidation ?? false, modifiers: this.modifiers })
            .then(renderer => {
            set.get(scene)?.update( /* empty */);
            // update dictionary with the actual renderer
            set.set(scene, renderer);
            // assign datasource immediately
            this._updatePipeline(scene);
            return renderer.canvas;
        }, (issue) => {
            this.log(Logging.LogLevel.Error, issue.message);
            this._webGPUNotSupported(issue);
            return null;
        });
    }
    _parseWgslModifiers(val) {
        const scripts = this.#items, vertexGeometryScript = scripts.find(a => a.type == 'vertex-input'), vertexOutputScript = scripts.find(a => a.type == 'vertex-output'), fragmentScript = scripts.find(a => a.type == 'fragment');
        const vertexInput = vertexGeometryScript?.wgsl, vertexOutput = vertexOutputScript?.wgsl, fragment = fragmentScript?.wgsl;
        this.modifiers = { vertexInput, vertexOutput, fragment, version: val };
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (first) {
            return;
        }
        switch (name) {
            case 'modifiers':
            case 'enableValidation':
                // reinitialize
                this._reinitialize();
                break;
        }
    }
    disconnectedCallback() {
        const set = this.#renderers;
        for (let [_, renderer] of set) {
            renderer?.dispose();
        }
        set.clear();
        this.#scenes.clear();
        super.disconnectedCallback();
    }
    dispose(scene) {
        const renderers = this.#renderers, scenes = this.#scenes;
        ;
        if (renderers.has(scene)) {
            renderers.get(scene)?.dispose();
            renderers.delete(scene);
        }
        if (scenes.has(scene)) {
            scenes.delete(scene);
        }
    }
    _reinitialize() {
        for (let scene of this.#scenes.values()) {
            // rebuild renderer with new config in association to scene
            this.initialize(scene);
        }
    }
    _ensureRenderer(scene) {
        const set = this.#renderers;
        return set.get(scene);
    }
    _updatePipeline(scene) {
        const renderer = this._ensureRenderer(scene);
        renderer?.update(...scene.datasource);
    }
    #sceneToUpdate;
    /**
     * Called when scene datasource changes.
     * @param scene
     */
    render(scene) {
        // do not render hidden stages
        const width = scene.size?.width, height = scene.size?.height;
        if (!(width > 0) || !(height > 0)) {
            return;
        }
        // retrieve stage renderer
        const renderer = this._ensureRenderer(scene);
        if (Utils.isNull(renderer)) {
            return;
        }
        if (this.#sceneToUpdate.has(scene)) {
            this.#sceneToUpdate.delete(scene);
            this._itemsChangedExec(scene);
        }
        // draw
        const pixel = GETVAL(scene, WEBGPU_RAYCAST_PIXEL_PROP);
        renderer.render({ cursor: pixel, time: performance.now(), resolution: { width, height } });
        // raycast, just in case
        if (!scene.interactive) {
            return;
        }
        const callback = GETVAL(scene, WEBGPU_RAYCAST_CALLBACK_PROP);
        if (Utils.isNullOrEmpty(callback)) {
            return;
        }
        if (Utils.isNullOrEmpty(pixel)) {
            callback(null);
            return;
        }
        // draw again
        renderer.raycast(pixel).then(array => {
            if (Utils.isNullOrEmpty(array)) {
                callback(null);
            }
            else {
                const retval = {
                    object: array[0], point: null
                };
                callback(retval);
            }
        });
    }
    snapshot(scene, background, type, quality) {
        const renderers = this.#renderers;
        if (!renderers.has(scene)) {
            return Promise.resolve(null);
        }
        const { canvas } = renderers.get(scene);
        const jpeg = !Utils.isNullOrEmpty(background), mime = type ?? (jpeg ? 'image/jpeg' : null), compression = quality ?? (jpeg ? .9 : null);
        return new Promise((resolve, _) => {
            this.render(scene);
            Utils.snapshotElement(canvas, background, mime, compression).then(resolve);
        });
    }
    raycast(stage, point, size, callback) {
        SETVAL(stage, WEBGPU_RAYCAST_CALLBACK_PROP, callback);
        if (point.x < 0 || point.y < 0 || point.x >= size.width || point.y >= size.height) {
            DELVAL(stage, WEBGPU_RAYCAST_PIXEL_PROP);
        }
        else {
            SETVAL(stage, WEBGPU_RAYCAST_PIXEL_PROP, point);
        }
    }
    _itemsChangedExec(stage) {
        this._updatePipeline(stage);
    }
    _itemsChanged(stage, _) {
        this.#sceneToUpdate.set(stage, true);
    }
    addItem(stage, item) {
        this._itemsChanged(stage, item);
    }
    removeItem(stage, item) {
        this._itemsChanged(stage, item);
    }
    updateItem(stage, item) {
        // if 'hide' property has changed or 'children' property (for groups)
        // then there's need to regenerate the tree view (camera switch, more/less meshes, ...)
        if (Utils.isNull(item)
            || item.flags.indexOf(StalePropertyFlag.Visibility) >= 0
            || item.flags.indexOf(StalePropertyFlag.Children) >= 0) {
            this._itemsChanged(stage, item);
        }
    }
    zoomFit(stage, ...items) {
        // TODO: implement on the renderer side
        return Promise.resolve();
    }
    project(stage, point3D) {
        // TODO: implement on the renderer side
        return { x: 0, y: 0 };
    }
    getScene(stage) {
        // no 3rd-party/proprietary scene objects to provide.
        return stage;
    }
};
__decorate([
    Debounce(100)
], Pacem3DWebgpuAdapterElement.prototype, "_parseWgslModifiers", null);
__decorate([
    Watch({ emit: false })
], Pacem3DWebgpuAdapterElement.prototype, "modifiers", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Boolean })
], Pacem3DWebgpuAdapterElement.prototype, "enableValidation", void 0);
Pacem3DWebgpuAdapterElement = __decorate([
    CustomElement({ tagName: P + '-3d-webgpu-adapter' })
], Pacem3DWebgpuAdapterElement);
export { Pacem3DWebgpuAdapterElement };
